import { useState } from 'react';
import type { ExerciseRow, WorkoutExerciseRow, WorkoutRow } from '@st/contract';
import { useSetWorkoutExercises, useUpdateWorkout } from '../../../api/queries';
import { useTranslation } from '../../../i18n/I18nProvider';
import { toMutationMessage } from '../../../i18n/errorMessage';
import { WORKOUT_TYPES, exerciseName, moveItem, prescriptionSummary } from '../../../lib/program';
import { cx } from '../../../lib/cx';
import {
  Badge,
  Button,
  EmptyState,
  Input,
  Modal,
  NumberInput,
  Select,
  Textarea,
  useToast,
} from '../../../ui';
import { ExercisePicker } from './ExercisePicker';
import { WORKOUT_TYPE_KEY } from './categories';

export interface WorkoutEditorProps {
  programId: string;
  workout: WorkoutRow;
  library: readonly ExerciseRow[];
  onClose: () => void;
}

/** Nova vježba dobiva id tek na serveru — do tada nosi ovaj placeholder. */
const DRAFT_ID = '__draft__';

function draftRow(workoutId: string, exercise: ExerciseRow, sortOrder: number): WorkoutExerciseRow {
  return {
    id: `${DRAFT_ID}${sortOrder}`,
    workoutId,
    exerciseId: exercise.id,
    sortOrder,
    sets: 3,
    reps: '10',
    weightHintKg: null,
    restSeconds: 90,
    tempo: null,
    trainerNoteHr: null,
    trainerNoteEn: null,
    supersetGroup: null,
    prescribedSets: [],
  };
}

/**
 * Sav rad s vježbama jednog treninga.
 *
 * Redoslijed ide optimistično jer je povratak trivijalan; svaka druga promjena
 * (dodavanje, uklanjanje, uređivanje) čeka server i osvježava stablo. U oba
 * slučaja šalje se CIJELA lista — nikad pojedinačna pozicija.
 */
export function WorkoutEditor({ programId, workout, library, onClose }: WorkoutEditorProps) {
  const { t, locale } = useTranslation();
  const { toast } = useToast();
  const setExercises = useSetWorkoutExercises(programId);
  const updateWorkout = useUpdateWorkout(programId);

  const [picking, setPicking] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [dragFrom, setDragFrom] = useState<number | null>(null);

  const rows = workout.exercises;

  function commit(next: WorkoutExerciseRow[], optimistic: boolean, conflictKey?: string) {
    setExercises.mutate(
      { workoutId: workout.id, rows: next, optimistic },
      {
        onError: (error) => toast(toMutationMessage(t, error, conflictKey), 'danger'),
      },
    );
  }

  function move(from: number, to: number) {
    if (to < 0 || to >= rows.length) return;
    commit(moveItem(rows, from, to), true);
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={workout.nameHr}
      closeLabel={t('a11y.closeDialog')}
      className="sm:max-w-content"
      footer={
        <Button size="sm" variant="secondary" onClick={onClose}>
          {t('action.done')}
        </Button>
      }
    >
      <WorkoutDetails
        workout={workout}
        pending={updateWorkout.isPending}
        onSave={(patch) =>
          updateWorkout.mutate(
            { workoutId: workout.id, patch },
            {
              onSuccess: () => toast(t('builder.saved'), 'success'),
              onError: (error) =>
                toast(toMutationMessage(t, error, 'builder.dayTaken'), 'danger'),
            },
          )
        }
      />

      <section className="mt-6" aria-label={t('builder.exercises')}>
        <div className="mb-3 flex items-center justify-between gap-3">
          <h3 className="font-display text-[18px] font-semibold text-ink">
            {t('builder.exercises')}
          </h3>
          <Button size="sm" onClick={() => setPicking((v) => !v)}>
            {t('builder.addExercise')}
          </Button>
        </div>

        {picking && (
          <div className="mb-4 rounded-xl border border-line-subtle bg-bg-alt p-4">
            <ExercisePicker
              library={library}
              onPick={(exercise) => {
                setPicking(false);
                commit([...rows, draftRow(workout.id, exercise, rows.length)], false);
              }}
            />
          </div>
        )}

        {rows.length === 0 ? (
          <EmptyState message={t('builder.noExercises')} />
        ) : (
          <>
            <p className="mb-2 text-[12px] text-ink-muted">{t('builder.reorderHint')}</p>
            <ol className="flex flex-col gap-2" data-testid="exercise-list">
              {rows.map((row, index) => {
                const name =
                  exerciseName(library, row.exerciseId, locale) ?? row.exerciseId;
                const open = expanded === row.id;
                return (
                  <li
                    key={row.id}
                    draggable
                    onDragStart={() => setDragFrom(index)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      if (dragFrom !== null && dragFrom !== index) move(dragFrom, index);
                      setDragFrom(null);
                    }}
                    onDragEnd={() => setDragFrom(null)}
                    className={cx(
                      'rounded-xl border border-line-subtle bg-surface p-3 shadow-card',
                      dragFrom === index && 'opacity-60',
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        aria-hidden="true"
                        className="mt-1 select-none text-[12px] text-ink-muted"
                      >
                        {index + 1}.
                      </span>
                      <button
                        type="button"
                        onClick={() => setExpanded(open ? null : row.id)}
                        aria-expanded={open}
                        className="min-w-0 flex-1 text-left"
                      >
                        <span className="block truncate text-[14.5px] text-ink">{name}</span>
                        <span className="block text-[12px] text-ink-muted">
                          {prescriptionSummary(row) || t('action.optional')}
                        </span>
                      </button>
                      {/*
                        Uklanjanje čeka server (`optimistic: false`), pa red do
                        odgovora ostaje na ekranu. Bez ove brave drugi klik je
                        računao iz iste, još nepromijenjene liste i time PONIŠTIO
                        prvo uklanjanje. Strelice ostaju otvorene — one pišu
                        optimistično, pa je njihova lista uvijek svježa.
                      */}
                      <span
                        className="flex shrink-0 items-center gap-1"
                        aria-busy={setExercises.isPending || undefined}
                      >
                        <IconButton
                          label={t('builder.moveUp')}
                          disabled={index === 0}
                          onClick={() => move(index, index - 1)}
                        >
                          ↑
                        </IconButton>
                        <IconButton
                          label={t('builder.moveDown')}
                          disabled={index === rows.length - 1}
                          onClick={() => move(index, index + 1)}
                        >
                          ↓
                        </IconButton>
                        <IconButton
                          label={t('builder.removeExercise')}
                          disabled={setExercises.isPending}
                          onClick={() =>
                            commit(
                              rows.filter((_, i) => i !== index),
                              false,
                            )
                          }
                        >
                          ×
                        </IconButton>
                      </span>
                    </div>

                    {open && (
                      <ExerciseDetails
                        row={row}
                        pending={setExercises.isPending}
                        onSave={(next) => {
                          commit(
                            rows.map((r, i) => (i === index ? next : r)),
                            false,
                          );
                          setExpanded(null);
                        }}
                      />
                    )}
                  </li>
                );
              })}
            </ol>
          </>
        )}
      </section>
    </Modal>
  );
}

function IconButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={cx(
        'h-8 w-8 rounded-md border border-line text-[14px] text-ink transition-colors duration-200',
        disabled ? 'opacity-30' : 'hover:bg-bg-alt',
      )}
    >
      <span aria-hidden="true">{children}</span>
    </button>
  );
}

function WorkoutDetails({
  workout,
  onSave,
  pending,
}: {
  workout: WorkoutRow;
  onSave: (patch: { nameHr: string; type: WorkoutRow['type']; estimatedMinutes: number | null }) => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(workout.nameHr);
  const [type, setType] = useState<WorkoutRow['type']>(workout.type);
  const [minutes, setMinutes] = useState<number | ''>(workout.estimatedMinutes ?? '');
  const [error, setError] = useState<string | undefined>(undefined);

  return (
    <section aria-label={t('builder.editWorkout')} className="grid gap-3 sm:grid-cols-3">
      <Input
        id="workout-name"
        label={t('builder.workoutName')}
        value={name}
        error={error}
        onChange={(e) => setName(e.target.value)}
      />
      <Select
        id="workout-type"
        label={t('builder.workoutType')}
        value={type}
        onChange={(e) => setType(e.target.value as WorkoutRow['type'])}
        options={WORKOUT_TYPES.map((v) => ({ value: v, label: t(WORKOUT_TYPE_KEY[v] ?? v) }))}
      />
      <NumberInput
        id="workout-minutes"
        label={t('builder.estimatedMinutes')}
        value={minutes}
        min={1}
        max={300}
        onValueChange={setMinutes}
      />
      <div className="sm:col-span-3">
        <Button
          size="sm"
          variant="secondary"
          loading={pending}
          onClick={() => {
            if (name.trim().length === 0) {
              setError(t('builder.nameRequired'));
              return;
            }
            setError(undefined);
            onSave({
              nameHr: name.trim(),
              type,
              estimatedMinutes: minutes === '' ? null : minutes,
            });
          }}
        >
          {t('common.save')}
        </Button>
      </div>
    </section>
  );
}

function ExerciseDetails({
  row,
  onSave,
  pending,
}: {
  row: WorkoutExerciseRow;
  onSave: (next: WorkoutExerciseRow) => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const [sets, setSets] = useState<number | ''>(row.sets ?? '');
  const [reps, setReps] = useState(row.reps ?? '');
  const [rest, setRest] = useState<number | ''>(row.restSeconds ?? '');
  const [tempo, setTempo] = useState(row.tempo ?? '');
  const [weight, setWeight] = useState<number | ''>(row.weightHintKg ?? '');
  const [note, setNote] = useState(row.trainerNoteHr ?? '');
  const [prescribed, setPrescribed] = useState(row.prescribedSets);

  function updateSet(index: number, patch: Partial<(typeof prescribed)[number]>) {
    setPrescribed((list) => list.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  }

  return (
    <div className="mt-3 flex flex-col gap-4 border-t border-line-subtle pt-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <NumberInput
          id={`sets-${row.id}`}
          label={t('builder.setsLabel')}
          value={sets}
          min={1}
          max={20}
          onValueChange={setSets}
        />
        <Input
          id={`reps-${row.id}`}
          label={t('builder.repsLabel')}
          hint={t('builder.repsHint')}
          value={reps}
          onChange={(e) => setReps(e.target.value)}
        />
        <NumberInput
          id={`rest-${row.id}`}
          label={t('builder.restLabel')}
          value={rest}
          min={0}
          max={900}
          onValueChange={setRest}
        />
        <NumberInput
          id={`weight-${row.id}`}
          label={t('builder.weightHint')}
          value={weight}
          min={0}
          max={1000}
          onValueChange={setWeight}
        />
        <Input
          id={`tempo-${row.id}`}
          label={t('builder.tempoLabel')}
          value={tempo}
          onChange={(e) => setTempo(e.target.value)}
        />
      </div>

      <Textarea
        id={`note-${row.id}`}
        label={t('builder.noteLabel')}
        rows={2}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />

      <div>
        <div className="mb-2 flex items-center justify-between gap-3">
          <span className="text-[13px] text-ink">
            {t('builder.prescribedSets')}{' '}
            <Badge tone="neutral">{prescribed.length}</Badge>
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              setPrescribed((list) => [
                ...list,
                {
                  id: `draft-${list.length}`,
                  workoutExerciseId: row.id,
                  setIndex: list.length,
                  reps: reps || null,
                  weightKg: weight === '' ? null : weight,
                  restSeconds: rest === '' ? null : rest,
                  note: null,
                },
              ])
            }
          >
            {t('builder.addPrescribedSet')}
          </Button>
        </div>
        <p className="mb-2 text-[12px] text-ink-muted">{t('builder.prescribedSetsHelp')}</p>

        {prescribed.map((p, i) => (
          <div key={p.id} className="mb-2 grid items-end gap-2 sm:grid-cols-[1fr_1fr_auto]">
            <Input
              id={`ps-reps-${row.id}-${i}`}
              label={t('training.setN', { n: i + 1 })}
              value={p.reps ?? ''}
              onChange={(e) => updateSet(i, { reps: e.target.value || null })}
            />
            <NumberInput
              id={`ps-weight-${row.id}-${i}`}
              label={t('builder.setWeight')}
              value={p.weightKg ?? ''}
              min={0}
              max={1000}
              onValueChange={(v) => updateSet(i, { weightKg: v === '' ? null : v })}
            />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setPrescribed((list) => list.filter((_, j) => j !== i))}
            >
              {t('builder.removePrescribedSet')}
            </Button>
          </div>
        ))}
      </div>

      <div>
        <Button
          size="sm"
          loading={pending}
          onClick={() =>
            onSave({
              ...row,
              sets: sets === '' ? null : sets,
              reps: reps.trim() || null,
              restSeconds: rest === '' ? null : rest,
              tempo: tempo.trim() || null,
              weightHintKg: weight === '' ? null : weight,
              trainerNoteHr: note.trim() || null,
              prescribedSets: prescribed.map((p, i) => ({ ...p, setIndex: i })),
            })
          }
        >
          {t('builder.saveExercise')}
        </Button>
      </div>
    </div>
  );
}
